import { makeStyles, Theme } from '@freighthub/silhouette-core'
import cn from 'classnames'
import { FC, MouseEvent, ReactNode } from 'react'
import { useI18n } from 'utils/localisation'

import { PdadStepUI } from '../service/PDAD'
import { PDAD_ICON_AIRPORT, PDAD_ICON_PALLETS, PDAD_ICON_POD, PDAD_ICON_POL, PDAD_ICON_WAREHOUSE } from './PdadIcon'

export const tpMileStoneUI: Record<string, PdadStepUI> = {
  cgomdUI: {
    icon: PDAD_ICON_WAREHOUSE,
    tooltipTerm: '',
    titleTerm: 'shipment_details-track_bar-cgomd',
    estimatedLabelTerm: 'shipment_details-track_bar-estimated_cgomd',
  },
  cgomoUI: {
    icon: PDAD_ICON_WAREHOUSE,
    tooltipTerm: '',
    titleTerm: 'shipment_details-track_bar-cgomd',
    estimatedLabelTerm: 'shipment_details-track_bar-estimated_cgomd',
  },
  cargoReadyUI: {
    icon: PDAD_ICON_PALLETS,
    tooltipTerm: 'shipments_list-progress_bar-cargo_ready',
    titleTerm: 'shipment_details-track_bar-cargo_ready',
    estimatedLabelTerm: 'shipment_details-track_bar-estimated_cargo_ready',
  },
  pickupUI: {
    icon: PDAD_ICON_WAREHOUSE,
    tooltipTerm: 'shipments_list-progress_bar-pickup',
    titleTerm: 'shipment_details-track_bar-pick_up',
    estimatedLabelTerm: 'shipment_details-track_bar-estimated_pick_up',
    confirmedLabelTerm: 'shipment_details-track_bar-confirmed_pick_up',
  },
  dprUI: {
    icon: PDAD_ICON_POL,
    tooltipTerm: 'shipments_list-progress_bar-dpr',
    titleTerm: 'shipment_details-track_bar-dpr',
    estimatedLabelTerm: 'shipment_details-track_bar-estimated_dpr',
  },
  departurePolUI: {
    icon: PDAD_ICON_POL,
    tooltipTerm: 'shipments_list-progress_bar-departure',
    titleTerm: 'shipment_details-track_bar-departure',
    estimatedLabelTerm: 'shipment_details-track_bar-estimated_departure',
    locationFallbackTerm: 'shipment_details-track_bar-origin_port',
  },
  transshipmentUI: {
    icon: PDAD_ICON_POL,
    tooltipTerm: 'shipments_list-progress_bar-transshipment',
    titleTerm: 'shipment_details-track_bar-transshipment',
    estimatedLabelTerm: 'shipment_details-track_bar-transshipment',
    locationFallbackTerm: 'shipment_details-track_bar-transshipment',
  },
  arrivalPodUI: {
    icon: PDAD_ICON_POD,
    tooltipTerm: 'shipments_list-progress_bar-arrival',
    titleTerm: 'shipment_details-track_bar-arrival',
    estimatedLabelTerm: 'shipment_details-track_bar-estimated_arrival',
    locationFallbackTerm: 'shipment_details-track_bar-destination_port',
  },
  deliveryUI: {
    icon: PDAD_ICON_WAREHOUSE,
    tooltipTerm: 'shipments_list-progress_bar-delivery',
    titleTerm: 'shipment_details-track_bar-delivery',
    estimatedLabelTerm: 'shipment_details-track_bar-estimated_delivery',
    confirmedLabelTerm: 'shipment_details-track_bar-confirmed_delivery',
  },
  arrivalRailUI: {
    icon: PDAD_ICON_POD,
    tooltipTerm: 'shipments_list-progress_bar-arrival',
    titleTerm: 'shipment_details-track_bar-arrival_terminal',
    estimatedLabelTerm: 'shipment_details-track_bar-estimated_arrival',
    locationFallbackTerm: 'shipment_details-track_bar-arrival_terminal',
  },
  departureRailUI: {
    icon: PDAD_ICON_POL,
    tooltipTerm: 'shipments_list-progress_bar-departure',
    titleTerm: 'shipment_details-track_bar-departure_terminal',
    estimatedLabelTerm: 'shipment_details-track_bar-estimated_departure',
    locationFallbackTerm: 'shipment_details-track_bar-departure_terminal',
  },
  departureAirportUI: {
    icon: PDAD_ICON_AIRPORT,
    tooltipTerm: 'shipments_list-progress_bar-departure_airport',
    titleTerm: 'shipment_details-track_bar-departure_airport',
    estimatedLabelTerm: 'shipment_details-track_bar-estimated_departure',
    locationFallbackTerm: 'shipment_details-track_bar-departure_airport',
  },
  destinationAirportUI: {
    icon: PDAD_ICON_AIRPORT,
    tooltipTerm: 'shipments_list-progress_bar-destination_airport',
    titleTerm: 'shipment_details-track_bar-destination_airport',
    estimatedLabelTerm: 'shipment_details-track_bar-estimated_arrival',
    locationFallbackTerm: 'shipment_details-track_bar-destination_airport',
  },
}

const useStyles = makeStyles<Theme>((theme) => ({
  cell: {
    flex: 1,
    '&:last-child': {
      flexGrow: 0,
      flexBasis: 24,
    },
    '&:first-child': {
      flexGrow: 0,
      flexBasis: 50,
    },
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 2),
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  rowClickable: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },
  },
  bold: {
    fontWeight: theme.typography.fontWeightMedium,
  },
}))

interface RowProps {
  onClick?: () => void
  className?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  [index: string]: any
}

export const Row: FC<RowProps> = ({ onClick, children, className, ...rest }) => {
  const classes = useStyles()
  return (
    <div onClick={onClick} className={cn(classes.row, className, { [classes.rowClickable]: onClick })} {...rest}>
      {children}
    </div>
  )
}

interface CellProps {
  className?: string
  children?: ReactNode
  onClick?: (e: MouseEvent<HTMLDivElement>) => void
}

export const Cell: FC<CellProps> = ({ className, children, ...props }) => {
  const classes = useStyles()
  return (
    <div className={cn(classes.cell, className)} {...props}>
      {children}
    </div>
  )
}

export const TPTableHeader: FC = () => {
  const { translate } = useI18n()
  const classes = useStyles()
  return (
    <Row className={classes.bold}>
      <Cell>#</Cell>
      <Cell>{translate('shipment_details-containers-container_number')}</Cell>
      <Cell>{translate('shipment_details-containers-type')}</Cell>
      <Cell>{translate('shipment_details-containers-seal_number')}</Cell>
      <Cell>{translate('shipment_details-containers-gross_weight')}</Cell>
      <Cell>{translate('shipment_details-containers-customer_reference')}</Cell>
      <Cell />
    </Row>
  )
}
